// SessionScreen.jsx — v2 live study session.
//
// One path, no flags. On mount:
//   POST /api/session               → create a session row, get sid
//   MediaRecorder(canvas+mic)       → 5s timeslices
//   each ondataavailable            → chunkQueue.enqueue(seq, blob)
//       → POST /api/session/{sid}/chunk (durable via IndexedDB retry queue)
//   each submit                     → POST /api/session/{sid}/boundary
//       (writes submits/qNN.png + appends boundaries.json; async scoring
//        fires server-side, result lands in session.json items[q_idx])
//   last question                   → rec.stop → queue.flush → /finalize
//       → navigate to /reel/{sid}
//
// No WebSocket. No RecordRTC. No hasDrawn frame gate. The legacy pipeline
// was deleted with the v2 rollout (plan step 5).

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api';
import { InkCanvas } from '../components/InkCanvas';
import { TeX } from '../components/Math';
import { ImportingScreen } from './ImportingScreen';
import { createChunkQueue } from '../session/chunkQueue';


const PSETS = {
  phys70_pset1: [
    {
      id: "phys70_pset1_q1",
      type: "derivation",
      domain: "physics",
      subject: "classical_mechanics",
      title: "From position to velocity",
      statement: "A particle's position is $$x(t) = 3t^2 - 2t + 5 \\quad (\\text{meters, seconds})$$",
      derivation: "Derive $v(t)$ from $x(t)$ without looking up the derivative formula — start from $$v = \\lim_{\\Delta t \\to 0} \\frac{\\Delta x}{\\Delta t}.$$",
    },
    {
      id: "phys70_pset1_q2",
      type: "derivation",
      domain: "physics",
      subject: "classical_mechanics",
      title: "From velocity to acceleration",
      statement: "Given $v(t)$ from Problem 1.",
      derivation: "Derive $a(t)$. Explain in one sentence why $a$ is constant for any $x(t)$ that is quadratic in $t$.",
    },
    {
      id: "phys70_pset1_q3",
      type: "derivation",
      domain: "physics",
      subject: "classical_mechanics",
      title: "Constant-acceleration kinematics",
      statement: "Starting from $a = \\tfrac{dv}{dt}$ and $v = \\tfrac{dx}{dt}$.",
      derivation: "Integrate to derive the three constant-acceleration equations: $$v = v_0 + a\\,t, \\quad x = x_0 + v_0\\,t + \\tfrac{1}{2} a\\,t^2, \\quad v^2 = v_0^2 + 2a(x - x_0).$$ Show every step.",
    },
    {
      id: "phys70_pset1_q4",
      type: "recall",
      domain: "physics",
      subject: "classical_mechanics",
      title: "Sanity check — cheetah vs cat",
      statement: "Cheetah: rest to $30\\,\\text{m/s}$ in $3.0\\,\\text{s}$. House cat: rest to $6\\,\\text{m/s}$ in $0.8\\,\\text{s}$.",
      derivation: "Compute $a$ in $\\text{m/s}^2$ and in multiples of $g$ for each. Which has the larger acceleration? One paragraph.",
    },
  ],
};


export function SessionScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const presetId = location.state?.presetId || null;
  const presetItems = location.state?.presetItems || null;
  const focusCourseId = location.state?.focusCourseId || null;
  const course = location.state?.course
    || (focusCourseId === 'phys70' ? { id: 'phys70', title: 'PHYS 70: Classical Mechanics' } : null);

  const onBack = () => navigate(-1);
  const onFinished = (sid) => {
    if (sid) navigate(`/reel/${sid}`);
    else navigate('/reel');
  };

  const [items, setItems] = React.useState([]);
  const [idx, setIdx] = React.useState(0);
  const [status, setStatus] = React.useState('starting…');
  const [ready, setReady] = React.useState(false);
  const [scoreBoard, setScoreBoard] = React.useState({});
  // When true, render the "saving session" loader instead of the canvas.
  // Set when the user clicks Submit on the last question; cleared when we
  // navigate to /reel. Rotating copy + spinner reuses ImportingScreen.
  const [finishing, setFinishing] = React.useState(false);

  // Session + recording state
  const sessionIdRef = React.useRef(null);
  const startedAtRef = React.useRef(0);
  const recorderRef = React.useRef(null);
  const queueRef = React.useRef(null);
  const seqRef = React.useRef(0);
  const canvasRef = React.useRef(null);
  const itemsRef = React.useRef([]);
  const idxRef = React.useRef(0);

  React.useEffect(() => { itemsRef.current = items; }, [items]);
  React.useEffect(() => { idxRef.current = idx; }, [idx]);

  // ─── Mount: load items, create session, start MediaRecorder ────────────
  React.useEffect(() => {
    let cancelled = false;
    let mediaStream = null;

    (async () => {
      // Load question list (preset from PlanReveal, or scheduler's next).
      const presetKey = presetId || (Array.isArray(presetItems) ? '__custom__' : null);
      let list;
      if (presetKey) {
        list = presetKey === '__custom__' ? presetItems : (PSETS[presetKey] || []);
      } else {
        const study = await api.getStudyNext();
        list = study.items || [];
      }
      if (cancelled) return;
      setItems(list);
      setIdx(0);

      // Create the session row. One POST, one sid, no WebSocket.
      let sid;
      try {
        const r = await fetch('/api/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            course_id: course?.id || focusCourseId || null,
            pset_id: presetId || null,
            items: list.map((it, i) => ({
              q_idx: i, item_id: it.id || '', title: it.title || '',
            })),
          }),
        });
        if (!r.ok) throw new Error(`POST /api/session ${r.status}`);
        sid = (await r.json()).sid;
      } catch (err) {
        console.error('[SessionScreen] create session failed:', err);
        setStatus('session create failed — reload to retry');
        return;
      }
      sessionIdRef.current = sid;
      startedAtRef.current = Date.now();
      queueRef.current = createChunkQueue({ sid });

      // Wait two rAFs so InkCanvas's resize() + redraw() have committed
      // before MediaRecorder samples the backing store.
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));
      const c = canvasRef.current;
      if (!c || typeof c.captureStream !== 'function') {
        setStatus('canvas unavailable — recording skipped');
        return;
      }
      try { c.ensurePainted?.(); } catch (_) {}
      const canvasEl = c.getCanvas?.();
      console.log('[SessionScreen] canvas dim:',
        canvasEl?.width, '×', canvasEl?.height,
        'css:', canvasEl?.clientWidth, '×', canvasEl?.clientHeight);

      const canvasStream = c.captureStream(25);
      const vTrack = canvasStream.getVideoTracks()[0];
      console.log('[SessionScreen] video track:',
        vTrack?.readyState, 'enabled:', vTrack?.enabled,
        'settings:', vTrack?.getSettings?.());
      if (!vTrack) {
        setStatus('video track unavailable — recording skipped');
        return;
      }

      const tracks = [vTrack];
      try {
        const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
        tracks.push(...mic.getAudioTracks());
      } catch (_) {
        // Mic denied — record silent. Session continues; scoring uses the submit image.
      }
      if (cancelled) return;
      mediaStream = new MediaStream(tracks);

      const hasAudio = mediaStream.getAudioTracks().length > 0;
      const mimeType = hasAudio
        ? 'video/webm;codecs=vp9,opus'
        : (window.MediaRecorder?.isTypeSupported('video/webm;codecs=vp9')
            ? 'video/webm;codecs=vp9' : 'video/webm');
      let rec;
      try {
        rec = new MediaRecorder(mediaStream, { mimeType, bitsPerSecond: 1_000_000 });
      } catch (err) {
        console.error('[SessionScreen] MediaRecorder start failed:', err);
        setStatus('recording unavailable — browser incompatible');
        return;
      }
      recorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (!e.data || e.data.size === 0) return;
        queueRef.current?.enqueue(seqRef.current++, e.data).catch(() => {});
      };
      rec.start(5000);
      setReady(true);
      setStatus('recording');
    })();

    return () => {
      cancelled = true;
      const rec = recorderRef.current;
      if (rec && rec.state !== 'inactive') { try { rec.stop(); } catch (_) {} }
      if (mediaStream) mediaStream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
    };
  }, []);

  // ─── Submit: /boundary per question; Finish: flush + finalize + navigate ─
  //
  // Ordering matters. Capture the canvas pixels + timestamp SYNCHRONOUSLY,
  // then advance + clear IMMEDIATELY. The /boundary POST fires in the
  // background so network RTT doesn't make the user see stale Q1 strokes
  // for ~300ms after they click Submit (and keep drawing during that
  // window, landing strokes in the wrong question's recording).
  const handleSubmit = async () => {
    const c = canvasRef.current;
    if (!c) return;
    const cur = itemsRef.current[idxRef.current] || {};
    const submittedIdx = idxRef.current;
    const sid = sessionIdRef.current;
    if (!sid) { setStatus('session not ready yet'); return; }

    // 1. Capture pixels + boundary time — BEFORE clearing the canvas.
    const submitDataURL = c.toDataURL('image/png');
    const tSeconds = (Date.now() - startedAtRef.current) / 1000;

    // 2. Pending chip in the score board.
    setScoreBoard((prev) => ({
      ...prev,
      [String(submittedIdx)]: {
        ...(prev[String(submittedIdx)] || {}),
        pending: true, title: cur.title || '',
      },
    }));

    const isLast = submittedIdx >= itemsRef.current.length - 1;

    // 3. Advance + clear IMMEDIATELY for non-last questions. The user sees
    //    the canvas wiped the instant they click Submit; any strokes from
    //    now on land in Q(n+1)'s recording window on the server.
    if (!isLast) {
      setIdx((i) => i + 1);
      idxRef.current = submittedIdx + 1;
      canvasRef.current?.clear();
    }

    // 4. /boundary POST. For non-last questions we fire-and-forget — the
    //    user is already on the next question. For the LAST question we
    //    MUST await it: /finalize below freezes boundaries.json into the
    //    derivation worker's input. If the last q_idx's boundary hasn't
    //    landed yet, the worker cuts N-1 per-question clips and the final
    //    question disappears from the Reel.
    const boundaryFetch = fetch(`/api/session/${sid}/boundary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        q_idx: submittedIdx,
        t_seconds: tSeconds,
        submit_image_b64: submitDataURL,
        item_id: cur.id || '',
        item_type: cur.type || 'derivation',
        question: { title: cur.title, q: cur.derivation || cur.statement || '' },
      }),
    }).catch((err) => console.warn('[SessionScreen] /boundary failed:', err));

    if (!isLast) return;
    // Last question — wait for its /boundary POST to land before stopping
    // the recorder. Without this, /finalize races with the last boundary
    // write and the Reel drops the final question.
    try { await boundaryFetch; } catch (_) {}

    // Last question — stop recorder, flush queue, finalize, navigate.
    // Switch the UI to the ImportingScreen loader so the user sees rotating
    // copy + spinner instead of a frozen canvas during the upload + worker
    // enqueue round-trip.
    setFinishing(true);
    setStatus('finishing session — saving recording…');
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      await new Promise((resolve) => {
        const done = () => resolve();
        rec.addEventListener('stop', done, { once: true });
        try { rec.stop(); } catch (_) { resolve(); }
      });
    }
    const queue = queueRef.current;
    if (queue) await queue.flush();

    let finalized = false;
    for (let attempt = 0; attempt < 3 && !finalized; attempt++) {
      const r = await fetch(`/api/session/${sid}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (r.status === 200) { finalized = true; break; }
      if (r.status === 202) {
        if (queue) await queue.flush();
        continue;
      }
      break;
    }
    if (!finalized) {
      await fetch(`/api/session/${sid}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: true }),
      });
    }

    onFinished(sid);
  };

  const fmtTime = (s) => {
    const m = String(Math.floor(s / 60)).padStart(2, '0');
    const sec = String(s % 60).padStart(2, '0');
    return m + ':' + sec;
  };

  const cur = items[idx] || {};
  const subjectLabel = (cur.subject || '').replace(/_/g, ' ');

  // Finishing the last question — reuse the ImportingScreen loader with
  // session-specific rotating copy. Same spinner + card shape the user
  // saw during syllabus ingest, so "we're working on it" feels consistent.
  if (finishing) {
    return (
      <ImportingScreen
        eyebrow="Wrapping up"
        label="Saving your session…"
        steps={[
          "Uploading the last video chunks…",
          "Stitching per-question clips…",
          "Extracting frames for AI review…",
          "Transcribing what you said…",
          "Asking Gemini to grade each question…",
          "Embedding the segments for search…",
        ]}
        hint="Usually 20–40 seconds. We'll drop you into the Reel when it's ready."
      />
    );
  }

  return (
    <section className="session">
      <div className="session-topbar">
        <button className="back-btn" onClick={() => {
          const rec = recorderRef.current;
          if (rec && rec.state !== 'inactive') { try { rec.stop(); } catch (_) {} }
          onBack();
        }}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 6l-6 6 6 6"/></svg>
          {course?.title || 'Study'}
        </button>
        <div className="session-progress">
          <span className="d-meta">Item {Math.min(idx + 1, items.length || 1)} of {items.length || '…'}</span>
          <div className="mini-bar"><div style={{width: `${items.length ? ((idx+1)/items.length)*100 : 0}%`}}/></div>
        </div>
        {/* Compact inline grading pills. Sits in the topbar so the
            absolute overlay never covers Next/Finish. Only renders after
            the first submit (scoreBoard non-empty), so it never pushes
            the canvas around while the user is mid-stroke. */}
        {Object.keys(scoreBoard).length > 0 && (
          <div className="session-scorepills">
            {items.map((q, i) => {
              const entry = scoreBoard[String(i)];
              if (!entry) return (
                <span key={i} className="sb-pill sb-pill-idle">Q{i + 1}</span>
              );
              if (entry.pending) return (
                <span key={i} className="sb-pill sb-pill-pending">Q{i + 1}…</span>
              );
              if (entry.error) return (
                <span key={i} className="sb-pill sb-pill-err">Q{i + 1} err</span>
              );
              const pct = entry.score != null ? Math.round(entry.score * 100) : null;
              const cls = pct == null ? 'sb-pill-idle' : pct >= 75 ? 'sb-pill-good' : pct >= 45 ? 'sb-pill-ok' : 'sb-pill-low';
              return (
                <span key={i} className={'sb-pill ' + cls} title={entry.feedback || ''}>
                  Q{i + 1}{pct != null ? ` · ${pct}%` : ''}
                </span>
              );
            })}
          </div>
        )}
        <span className={'capture-pill' + (ready ? ' capture-pill--on' : ' capture-pill--wait')}>
          <span className="capture-dot"/>
          {ready ? 'recording' : (status || 'starting…')}
        </span>
      </div>

      <div className="session-question">
        <div className="question-eyebrow">
          {cur.domain ? `${cur.domain} · ${subjectLabel}` : 'Loading…'}
        </div>
        <div className="question-serif">{cur.title || ' '}</div>
        {cur.statement && <TeX as="div" className="question-body">{cur.statement}</TeX>}
        {cur.derivation && <TeX as="div" className="question-body">{cur.derivation}</TeX>}
      </div>

      <div className="canvas">
        <InkCanvas canvasRef={canvasRef} />
      </div>

      <div className="session-actions">
        <button className="btn-ghost" onClick={() => canvasRef.current?.undo()}>Undo</button>
        <button className="btn-ghost" onClick={() => canvasRef.current?.clear()}>Clear</button>
        {items.length > 1 && (
          <>
            <button
              className="btn-ghost"
              onClick={() => { setIdx(i => Math.max(0, i - 1)); canvasRef.current?.clear(); }}
              disabled={idx === 0}
            >← Prev</button>
            <button
              className="btn-ghost"
              onClick={() => { setIdx(i => Math.min(items.length - 1, i + 1)); canvasRef.current?.clear(); }}
              disabled={idx >= items.length - 1}
            >Next →</button>
          </>
        )}
        <button className="btn-primary" onClick={handleSubmit} disabled={items.length === 0}>
          {idx >= items.length - 1 ? 'Finish' : 'Submit & next'}
        </button>
      </div>
      {status && <div className="session-status">{status}</div>}
    </section>
  );
}
